import { isAstEqual } from './equal.utils';
import { generateResourceName } from './name.utils';
import { buildAstState } from '..';
import { ImageArrayAttribute } from '@/services/shape/attributes/image-array';
import { astToXml } from '@/utils/xmlTool';
export { insertImages } from './resource-image.utils';

const _handleRecolor = (target: Ast) => {
  // 如果supportRecolor设置不合适，需要修改
  // 相册/人像表盘下，必须设置为重着色，其他情况下，根据配色数量确定
  const isMultiColor = (buildAstState.data.colorTable?.length ?? 0) > 1;
  const isRecolorMode = buildAstState.data.colorMode == 'recolor';
  let supportRecolor = false;
  if (isRecolorMode) {
    // 需要区分，是否为单一息屏
    const { type, isSingleAOD } = buildAstState.data.theme ?? {};
    if (type == 'AOD' && isSingleAOD) {
      // 单一息屏，不支持，其他都支持
      supportRecolor = false;
    } else {
      supportRecolor = true;
    }
  } else {
    supportRecolor = isMultiColor;
  }
  // 在不支持的基础下，更正已设置为支持的属性
  if (!supportRecolor) {
    if (target.attrsMap.supportRecolor == 'true') {
      target.attrsMap.supportRecolor = 'false';
    }
    if (target.attrsMap.recolorEnable == 'true') {
      target.attrsMap.recolorEnable = 'false';
    }
  }
};

/**
 * 插入资源元素（会自动生成name），并返回新增的资源ast；
 * @param resourceAst
 * @returns ast
 */
export const addResourceIfNotExists = (
  resourceAst: Ast,
  target: Ast,
  extraCheckEqual?: () => boolean,
  keepName?: boolean
) => {
  // 再次处理recolor属性，防止前面设置错误
  _handleRecolor(target);

  // 不可使用复用资源的设备
  const excludeTypes = ['M66'];
  // 在导出时判断是否复用
  const reuseEnabled =
    buildAstState.options.buildFor == 'export' &&
    !excludeTypes.includes(buildAstState.data.description?.deviceType ?? '');

  const res =
    // editor内部使用时不复用资源
    reuseEnabled
      ? resourceAst.children.find((item) => {
          return isAstEqual(item, target);
        })
      : undefined;
  if (res && (extraCheckEqual ? extraCheckEqual?.() : true)) {
    // 查找到可复用的元素
    if (
      buildAstState.options.buildFor == 'editor' &&
      ['Slot', 'Widget'].includes(res.tag)
    ) {
      res.children.forEach((item, index) => {
        const _target = target.children[index];
        const _targetId = _target?.attrsMap?._id;
        if (!_targetId) return;
        const _ids = item.attrsMap._id?.split(',') ?? [];
        if (!_ids.includes(_targetId)) _ids.push(_targetId);
        item.attrsMap._id = _ids.join(',');
      });
    }
    return res;
  }

  if (!keepName || !target.attrsMap.name) {
    // 统一添加name
    const name = generateResourceName(resourceAst, target.tag);
    delete target.attrsMap.name;
    target.attrsMap = {
      name,
      ...target.attrsMap
    };
  }
  resourceAst.children.push(target);
  return target;
};

export const generateLayoutAst = (
  data: {
    ref: string;
    x: number;
    y: number;
  },
  tag: 'Layout' | 'Item' = 'Layout'
) => {
  const { ref, x, y } = data;
  const layoutAst: Ast = {
    tag,
    attrsMap: {
      ref,
      x: String(x),
      y: String(y)
    },
    children: []
  };
  return layoutAst;
};

/**
 * 获取通过widget拼接的image array的最大宽度，一般用于时间和日期组合数据源下
 * @param data
 * @returns
 */
export const getImageArrayWidgetMaxSize = (options: {
  resources: ImageArrayAttribute['resources'];
  format: '08:00' | '08:00:00' | '08月16日' | '08/16' | 'SPF08';
  // 单位符号前后的间距
  gap: number;
  // 数字图片直接的间距
  space: number;
}) => {
  const getImgSize = buildAstState.data.getImgSize;

  const { resources, format, gap = 0, space = 0 } = options;
  const sizeMap: {
    [key in 'images' | 'unitIcon' | 'unitIcon2']?: {
      height: number;
      width: number;
    };
  } = {};
  ['images', 'unitIcon', 'unitIcon2'].forEach((key) => {
    let _height = 0;
    let _width = 0;
    Object.values(resources[key as 'images'] ?? {})?.[0]?.forEach((imgkey) => {
      const img = getImgSize?.(imgkey);
      _height = Math.max(_height, img?.height ?? 0);
      _width = Math.max(_width, img?.width ?? 0);
    });
    sizeMap[key as 'images'] = {
      height: _height,
      width: _width
    };
  });

  // 默认情况下按照08:00或08/00格式处理
  const res = {
    width: 0,
    height: 0
  };
  if (format == '08/16' || format == '08:00') {
    const { width = 0, height = 0 } = sizeMap['images'] ?? {};
    const { width: width2 = 0, height: height2 = 0 } =
      sizeMap['unitIcon'] ?? {};
    res.width = 2 * space + gap * 2 + width * 4 + width2;
    res.height = Math.max(height, height2);
  } else if (format == '08:00:00') {
    // 时分秒：6个数字 + 2个分隔符
    const { width = 0, height = 0 } = sizeMap['images'] ?? {};
    const { width: width2 = 0, height: height2 = 0 } =
      sizeMap['unitIcon'] ?? {};
    res.width = 4 * space + gap * 4 + width * 6 + width2 * 2;
    res.height = Math.max(height, height2);
  } else if (format == '08月16日') {
    const { width = 0, height = 0 } = sizeMap['images'] ?? {};
    const { width: width2 = 0, height: height2 = 0 } =
      sizeMap['unitIcon'] ?? {};
    const { width: width3 = 0, height: height3 = 0 } =
      sizeMap['unitIcon2'] ?? {};
    res.width = 2 * space + gap * 3 + width * 4 + width2 + width3;
    res.height = Math.max(height, height2, height3);
  } else if (format == 'SPF08') {
    const { width = 0, height = 0 } = sizeMap['images'] ?? {};
    const { width: width2 = 0, height: height2 = 0 } =
      sizeMap['unitIcon'] ?? {};
    // 宽度=icon +
    res.width = 1 * space + gap * 1 + width * 2 + width2;
    res.height = Math.max(height, height2);
  }

  return res;
};

export const sortResource = (resourceAst: Ast) => {
  const _sortByTag = (tag: string) => {
    const part1 = resourceAst.children.filter((item) => item.tag == tag);
    const part2 = resourceAst.children.filter((item) => item.tag != tag);
    resourceAst.children = [...part1, ...part2];
  };
  // 获取所有的tag
  const tags = resourceAst.children.map((item) => item.tag);
  // 按下面进行排序
  const sortTags = [
    'Translation',
    'Image',
    'File',
    'ImageArray',
    'File',
    'SpriteGroup',
    'Sprite',
    'DataItemImageNumber',
    'DataItemImageValues',
    'DataItemPointer',
    'DataItemText',
    'DataItemLineProgressBar',
    'DataItemArcProgressBar',
    'Animation',
    'Condition',
    'Widget',
    'Slot'
  ];
  // 根据所有的tags,补充排序，防止出现未知的tag
  tags.forEach((tag) => {
    if (!sortTags.includes(tag)) sortTags.unshift(tag);
  });

  // 逆序排序
  sortTags.reverse().forEach((tag) => {
    _sortByTag(tag);
  });
};

export const delUslessingResource = (rootAst: Ast) => {
  const xml = astToXml(rootAst);
  const resouecesAst = rootAst.children.find((item) => item.tag == 'Resources');
  if (!resouecesAst) return;
  resouecesAst.children = resouecesAst.children.filter((item) => {
    const { tag, attrsMap } = item;
    const { name } = attrsMap;
    if (tag != 'Image' && tag != 'ImageArray') return true;
    return xml.includes('@' + name);
  });
};

export const removeColorGroupWithWidgetImageResource = (ast: Ast) => {
  const deleteColorGroup = (imageAst: Ast) => {
    delete imageAst.attrsMap.colorGroup;
  };
  ast.children.forEach((item) => {
    // 不是widget的可以不用管
    const isWidget = item.tag === 'Widget';
    if (!isWidget) return;

    // widget 上面的editBox、preview属性对应的Image都需要处理
    const { editBox, preview } = item.attrsMap;
    const names = [editBox, preview]
      .filter((it) => !!it)
      .map((it) => it.slice(1));

    // 遍历查找到对应的image，然后移除colorGroup属性
    ast.children
      .filter(
        (item) => item.tag === 'Image' && names.includes(item.attrsMap.name)
      )
      .forEach((it) => deleteColorGroup(it));
  });
};

export const removeCustomAttributes = (ast: Ast) => {
  for (const key in ast.attrsMap) {
    if (key.startsWith('_')) delete ast.attrsMap[key];
  }
  if (ast.children.length) {
    for (const item of ast.children) {
      removeCustomAttributes(item);
    }
  } else return;
};

export const removeDuplicateLayout = (ast: Ast) => {
  // 将ast theme的layout去重
  const themes = ast.children.filter((item) => item.tag == 'Theme');
  themes.forEach((theme) => {
    // 将theme children的layout去重
    const _layouts: Ast[] = [];
    theme.children.forEach((item) => {
      // 如果没有重复的，则添加
      if (!_layouts.find((it) => isAstEqual(it, item))) {
        _layouts.push(item);
      }
    });
    theme.children = _layouts;
  });
};
